import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import ExpandableTable from "components/ExpandableTable";
import {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import SnackbarMessage from "components/SnackbarMessage";
import useGlobalContext from "hooks/globalData";
import useListTitle from "hooks/useListTitle";
import { useMemo, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  ListRequestOfWebhookFilter,
  ListResponseOfWebhookNotificationViewModel,
  WebhookFilter,
  WebhookRepostApiRequest,
} from "services/KnowfullyAdminClient";
import persistanceStore from "stores/persistanceStore";
import { Severity, SnackbarMessageProps } from "types/common-types";
import { standardDateFormat } from "utils/dateUtils";
import { hasUserPermission } from "helpers/ClaimHelpers";
import { ClaimPermission, ParentClaim } from "types/claimTypes";

const WebhooksList = () => {
  const { formatMessage: t } = useIntl();
  const { webhooksService } = useGlobalContext();
  const queryClient = useQueryClient();
  const [order, setOrder] = useState<TableSortType>("desc");
  const [orderBy, setOrderBy] = useState("EventDate");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [snackbarMessage, setSnackbarMessage] =
    useState<SnackbarMessageProps | null>(null);

  const webhookStartDate = persistanceStore.useState(
    (s) => s.webhookStartDate
  );
  const webhookEndDate = persistanceStore.useState((s) => s.webhookEndDate);
  const webhookHasSubscriptionId = persistanceStore.useState(
    (s) => s.webhookHasSubscriptionId
  );
  const webhookSearchTerm = persistanceStore.useState(
    (s) => s.webhookSearchTerm
  );

  const today = useMemo(() => new Date(), []);
  const startDate = webhookStartDate ?? today;
  const endDate = webhookEndDate ?? today;

  useListTitle("webhooksLabel");

  const canRepost = hasUserPermission(
    ParentClaim.Operations,
    "webhooks",
    ClaimPermission.Repost
  );

  const { data, isFetching } =
    useQuery<ListResponseOfWebhookNotificationViewModel>(
      [
        "webhooksService.getNotifications",
        startDate?.toISOString(),
        endDate?.toISOString(),
        webhookHasSubscriptionId,
        webhookSearchTerm,
        page,
        rowsPerPage,
        orderBy,
        order,
      ],
      () =>
        webhooksService.getNotifications(
          ListRequestOfWebhookFilter.fromJS({
            pageNumber: page,
            pageSize: rowsPerPage,
            sortField: orderBy,
            sortDescending: order === "desc",
            parameters: WebhookFilter.fromJS({
              beginDate: startDate,
              endDate: endDate,
              hasSubscriptionId: webhookHasSubscriptionId,
              searchTerm: webhookSearchTerm || undefined,
            }),
          })
        ),
      { keepPreviousData: true }
    );

  const handleRepostMultiple = async (ids: string[]) => {
    try {
      const response = await webhooksService.repostNotifications(
        WebhookRepostApiRequest.fromJS({ notificationIds: ids })
      );
      const results = response.data ?? [];
      const successCount = results.filter((r) => r.success).length;
      const failureCount = results.length - successCount;

      if (failureCount === 0) {
        setSnackbarMessage(
          new SnackbarMessageProps(
            t({ id: "resendSuccess" }, { count: successCount }),
            Severity.Success
          )
        );
      } else {
        setSnackbarMessage(
          new SnackbarMessageProps(
            t(
              { id: "resendPartialFailure" },
              { successCount, failureCount }
            ),
            Severity.Warning
          )
        );
      }

      queryClient.invalidateQueries("webhooksService.getNotifications");
    } catch (err: any) {
      setSnackbarMessage(
        new SnackbarMessageProps(
          err?.response?.data?.data ?? "Repost failed",
          Severity.Error
        )
      );
    }
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "notificationId",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "notificationIdLabel" }),
      noSort: true,
    },
    {
      accessor: "eventDate",
      align: "left",
      width: 0,
      type: "node",
      label: t({ id: "dateTimeLabel" }),
      View: ({ value }: { value: { eventDate?: string | null } }) => (
        <span style={{ whiteSpace: "nowrap" }}>
          {value?.eventDate
            ? value.eventDate.replace("T", " ").slice(0, 19)
            : null}
        </span>
      ),
    },
    {
      accessor: "deliveryStatus",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "deliveryStatusLabel" }),
      noSort: true,
    },
    {
      accessor: "eventType",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "eventTypeLabel" }),
      noSort: true,
    },
    {
      accessor: "transactionId",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "transactionIdLabel" }),
      noSort: true,
    },
    {
      accessor: "invoiceNumber",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "invoiceNumberLabel" }),
      noSort: true,
    },
    {
      accessor: "amount",
      align: "right",
      width: 0,
      type: "text",
      label: t({ id: "amountLabel" }),
      noSort: true,
    },
    {
      accessor: "subscriptionId",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "subscriptionIdLabel" }),
      noSort: true,
    },
  ];

  const rows = (data?.data ?? [])
    .filter(
      (row): row is typeof row & { notificationId: string } =>
        !!row.notificationId
    )
    .map((row) => ({
      ...row,
      id: row.notificationId,
    }));

  return (
    <Container>
      <Box mb={3}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              label={t({ id: "startDateLabel" })}
              value={startDate || null}
              format={standardDateFormat}
              onChange={(date: any) =>
                persistanceStore.update((s) => {
                  s.webhookStartDate = date ?? undefined;
                })
              }
              slotProps={{
                textField: { fullWidth: true, variant: "standard" },
                actionBar: { actions: ["clear", "cancel"] },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              label={t({ id: "endDateLabel" })}
              value={endDate || null}
              format={standardDateFormat}
              onChange={(date: any) =>
                persistanceStore.update((s) => {
                  s.webhookEndDate = date ?? undefined;
                })
              }
              slotProps={{
                textField: { fullWidth: true, variant: "standard" },
                actionBar: { actions: ["clear", "cancel"] },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={webhookHasSubscriptionId}
                  onChange={(e) =>
                    persistanceStore.update((s) => {
                      s.webhookHasSubscriptionId = e.target.checked;
                    })
                  }
                />
              }
              label={t({ id: "hasSubscriptionIdLabel" })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={webhookSearchTerm ?? ""}
              placeholder={t({ id: "searchBySubscriptionId" })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: webhookSearchTerm ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() =>
                        persistanceStore.update((s) => {
                          s.webhookSearchTerm = "";
                        })
                      }
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              onChange={(e) =>
                persistanceStore.update((s) => {
                  s.webhookSearchTerm = e.target.value;
                })
              }
            />
          </Grid>
        </Grid>
      </Box>

      <ExpandableTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows,
          loading: isFetching,
          dense: true,
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          ...(canRepost && {
            executeMultiple: {
              onExecuteMultiple: handleRepostMultiple,
              dialogLabel: t({ id: "resendConfirmation" }),
              buttonLabel: t({ id: "resendButton" }),
            },
          }),
          titleComponent: (
            <Typography variant="h6">
              <T id="webhooksLabel" />
            </Typography>
          ),
          collapsable: true,
          hideEmptyRows: true,
        }}
      />

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default WebhooksList;
